import React, { useEffect, useState } from 'react';
import { SimpleGrid, useToast } from '@chakra-ui/react';
import {
  MdAgriculture,
  MdApartment,
  MdAssignment,
  MdBarChart,
  MdConstruction,
  MdGroups,
  MdInventory2,
  MdMap,
  MdPeopleAlt,
  MdSettings,
} from 'react-icons/md';
import { getDashboardCounts } from '../../../repositories/dashboard.repository';
import {
  HubCard,
  HubError,
  HubLoading,
  HubPageHeader,
  HubSectionLabel,
} from './hubUi';

export default function HubHome({ push }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setData(await getDashboardCounts());
    } catch (e) {
      setError(e.message || 'Failed to load dashboard');
      toast({ title: e.message || 'Failed to load dashboard', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <HubLoading />;
  if (error) return <HubError message={error} onRetry={load} />;

  const eq = data?.equipment || {};
  const sp = data?.vendors?.serviceProviders?.total || 0;
  const ag = data?.vendors?.agencies?.total || 0;
  const members = sp + ag || data?.vendors?.total || 0;
  const indents = data?.indents || {};
  const bus = data?.businessUnits || {};

  return (
    <>
      <HubPageHeader
        title="Dashboard hub"
        subtitle="Live totals from the API. Click a card to drill into that area — lists, filters, and details stay inside Hub view."
      />

      <HubSectionLabel>At a glance</HubSectionLabel>
      <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing={4} mb={8}>
        <HubCard
          icon={MdAgriculture}
          label="Total equipment"
          value={String(eq.total ?? 0)}
          hint={`${eq.pendingKyc ?? 0} pending KYC · ${eq.deployed ?? 0} deployed`}
          onClick={() => push({ id: 'equipmentHub', label: 'Equipment' })}
        />
        <HubCard
          icon={MdPeopleAlt}
          label="Members"
          value={String(members)}
          hint={`${data?.vendors?.pending ?? 0} pending verification`}
          onClick={() => push({ id: 'membersHub', label: 'Members' })}
        />
        <HubCard
          icon={MdAssignment}
          label="Open indents"
          value={String(indents.pending ?? 0)}
          hint={`${indents.total ?? 0} total · ${indents.assigned ?? 0} assigned`}
          onClick={() => push({ id: 'indentsHub', label: 'Indents' })}
        />
        <HubCard
          icon={MdGroups}
          label="Staff & admin"
          value={String(data?.staff?.total ?? 0)}
          hint={`${data?.staff?.active ?? 0} active · directory · config`}
          onClick={() => push({ id: 'staffHub', label: 'Staff' })}
        />
      </SimpleGrid>

      <HubSectionLabel>Master data</HubSectionLabel>
      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4} mb={8}>
        <HubCard
          icon={MdMap}
          label="Villages"
          value={String(data?.villages?.total ?? 0)}
          hint="Add / edit villages →"
          onClick={() => push({ id: 'masterVillages', label: 'Villages' })}
        />
        <HubCard
          icon={MdApartment}
          label="Business units"
          value={String(bus.total ?? 0)}
          hint={`${bus.sites ?? 0} sites · add / edit →`}
          onClick={() =>
            push({ id: 'masterBusinessUnits', label: 'Business units' })
          }
        />
        <HubCard
          icon={MdSettings}
          label="Master data hub"
          value="Open"
          hint="Villages · BUs · equipment types"
          onClick={() => push({ id: 'masterHub', label: 'Master data' })}
        />
      </SimpleGrid>

      <HubSectionLabel>Reports</HubSectionLabel>
      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4}>
        <HubCard
          icon={MdBarChart}
          label="Village report"
          value={String(data?.villages?.total ?? 0)}
          hint="Utilization by village"
          onClick={() => push({ id: 'villageReport', label: 'Village report' })}
        />
        <HubCard
          icon={MdConstruction}
          label="BU / site report"
          value={String(bus.sites ?? 0)}
          hint={`${bus.total ?? 0} business units · utilization`}
          onClick={() => push({ id: 'buReport', label: 'BU utilization' })}
        />
        <HubCard
          icon={MdInventory2}
          label="Materials"
          value={String(data?.materials?.total ?? 0)}
          hint={`${data?.materials?.pending ?? 0} pending review`}
          onClick={() => push({ id: 'membersHub', label: 'Members' })}
        />
      </SimpleGrid>
    </>
  );
}
